import React from 'react';
import { SagaEditor, isTableRow, EditorOperations } from '@saga/shared';
import { ReactEditor, useSlate } from 'slate-react';
import { Node, NodeEntry } from 'slate';

// Import context from TableContexts.tsx
import { TableSelectionContext } from './TableContexts';

// Helper hook for accessing context
const useTableSelection = () => {
    const context = React.useContext(TableSelectionContext);
    if (!context) {
        throw new Error('useTableSelection must be used inside a TableContextProvider');
    }
    return context;
};

function getNodeEntry<T extends Node>(editor: ReactEditor, element: T): NodeEntry<T> {
    const path = ReactEditor.findPath(editor, element);
    return [element, path];
}

export const tableRowPlugin = SagaEditor.Plugins.createBlockPlugin({
    match: isTableRow,
    Component({ element, children }) {
        const { onRowSelectionChange } = useTableSelection();
        const editor = useSlate();
        const nodeEntry = getNodeEntry(editor, element);
        const rowIndex = nodeEntry[1][nodeEntry[1].length - 1];

        // Check if this row is selected using efficient row selection check
        const rowSelected = EditorOperations.Tables.isRowSelected(editor, nodeEntry);

        // Report selection status to parent table
        React.useEffect(() => {
            onRowSelectionChange(rowIndex, rowSelected);
        }, [rowSelected, rowIndex, onRowSelectionChange]);

        return (
            <tr id={element.id} className="divide-x dark:divide-zinc-600">
                {children}
            </tr>
        );
    },
});
